/**
 * HTML 导出服务
 * 将 Markdown 内容转换为 HTML 文件
 */

import { marked } from 'marked'
import { ExportOptions } from '../../../types/export'
import { downloadBlob } from '../../../utils/download'
import { generateExportFilename } from './exportStyles'

/**
 * 生成完整的 HTML 文档
 */
const generateHTML = (content: string, title?: string): string => {
  // 配置 marked
  marked.setOptions({
    breaks: true,
    gfm: true,
  })
  
  // 转换 Markdown 为 HTML
  const htmlContent = marked(content)
  
  // 生成完整的 HTML 文档
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title || 'Markdown Document'}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            background-color: #fff;
        }
        h1, h2, h3, h4, h5, h6 {
            margin-top: 2em;
            margin-bottom: 1em;
            font-weight: 600;
            line-height: 1.25;
        }
        h1 {
            font-size: 2em;
            border-bottom: 1px solid #eaecef;
            padding-bottom: 0.3em;
        }
        h2 {
            font-size: 1.5em;
            border-bottom: 1px solid #eaecef;
            padding-bottom: 0.3em;
        }
        h3 {
            font-size: 1.25em;
        }
        p {
            margin-bottom: 1em;
        }
        a {
            color: #0366d6;
            text-decoration: none;
        }
        a:hover {
            text-decoration: underline;
        }
        code {
            background-color: #f6f8fa;
            border-radius: 3px;
            font-size: 85%;
            margin: 0;
            padding: 0.2em 0.4em;
        }
        pre {
            background-color: #f6f8fa;
            border-radius: 6px;
            font-size: 85%;
            line-height: 1.45;
            overflow: auto;
            padding: 16px;
        }
        pre code {
            background-color: transparent;
            border: 0;
            display: inline;
            line-height: inherit;
            margin: 0;
            max-width: auto;
            overflow: visible;
            padding: 0;
            word-wrap: normal;
        }
        blockquote {
            border-left: 4px solid #dfe2e5;
            color: #6a737d;
            padding: 0 1em;
            margin: 0 0 1em 0;
        }
        ul, ol {
            padding-left: 2em;
            margin-bottom: 1em;
        }
        table {
            border-collapse: collapse;
            width: 100%;
            margin-bottom: 1em;
        }
        th, td {
            border: 1px solid #dfe2e5;
            padding: 6px 13px;
        }
        th {
            background-color: #f6f8fa;
            font-weight: 600;
        }
        tr:nth-child(even) {
            background-color: #f6f8fa;
        }
        img {
            max-width: 100%;
            height: auto;
        }
        hr {
            border: none;
            border-top: 1px solid #eaecef;
            height: 1px;
            margin: 2em 0;
        }
    </style>
</head>
<body>
    ${title ? `<h1>${title}</h1>` : ''}
    ${htmlContent}
</body>
</html>`
}

/**
 * 导出为 HTML 文件
 */
export const exportToHTML = async (
  content: string,
  options: ExportOptions
): Promise<void> => {
  try {
    // 生成 HTML 内容
    const htmlContent = generateHTML(content, options.title)
    
    // 创建 blob
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' })
    
    // 下载文件
    const filename = generateExportFilename('html', options.filename)
    downloadBlob(blob, filename)
    
  } catch (error) {
    console.error('HTML 导出失败:', error)
    throw new Error(`HTML 导出失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 预览 HTML 内容
 */
export const previewHTML = (
  content: string,
  options: ExportOptions
): string => {
  try {
    return generateHTML(content, options.title)
  } catch (error) {
    console.error('HTML 预览失败:', error)
    return '预览生成失败'
  }
}

/**
 * 检查浏览器是否支持 HTML 导出
 */
export const checkHTMLSupport = (): boolean => {
  // HTML 导出只需要 Blob API 支持
  return typeof Blob !== 'undefined' && typeof URL !== 'undefined'
}